import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { get } from "lodash";
import {
  consolidatePortfolioChartData,
  formatTimeSeriesChartData,
} from "@shared/utils/charts";
import { createSelector } from "@reduxjs/toolkit";
import { BotType, GetTradesRequest, TimeRange } from "@shared/types";
import { dashboardFilters } from "@shared/slices/filters.slice";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import { filterTrades } from "@shared/features/trades/common/utils";
import { filterBots } from "@shared/features/dashboard/utils";
import { selectBotsData as selectBotsDataFromSlice } from "@shared/slices/bots.slice";

const selectConsolidatedPortfolioHistoryData = (timeRange: TimeRange) =>
  createSelector(
    sharedLib.sharedApi.portfolio.endpoints.getAmountsHistory.select({
      time_range: timeRange,
    }),
    dashboardFilters.selectors.selectClientsFilter,
    dashboardFilters.selectors.selectExchangeToClientsMapping,
    (
      portfolioHistoryData,
      portfolioClientsFilter,
      exchangeToClientsMapping
    ) => {
      return consolidatePortfolioChartData(
        portfolioHistoryData?.data,
        timeRange,
        portfolioClientsFilter,
        exchangeToClientsMapping
      );
    }
  );

const selectAccountChartData = (timeRange: TimeRange) =>
  createSelector(
    selectConsolidatedPortfolioHistoryData(timeRange),
    sharedLib.sharedApi.portfolio.endpoints.getAmountsHistory.select({
      time_range: timeRange,
    }),
    selectDisplayCurrency,
    (consolidatedPortfolioHistoryData, rawData, displayCurrency) => {
      const yValueIndex = displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1;
      const precision = get(
        rawData.data,
        ["precision", "balance", displayCurrency],
        2
      );
      return formatTimeSeriesChartData(
        consolidatedPortfolioHistoryData,
        yValueIndex,
        true,
        null,
        precision
      );
    }
  );

const selectOpenTrades = (args?: GetTradesRequest) =>
  createSelector(
    sharedLib.sharedApi.trades.endpoints.getOpenTradesData.select(args),
    dashboardFilters.selectors.selectFilters,
    (tradesData, filtersSlice) => {
      return filterTrades(tradesData?.data?.data || [], filtersSlice.filters);
    }
  );

const selectBotsData = (botType: BotType) =>
  createSelector(
    selectBotsDataFromSlice({ bot_type: botType, include_archived: false }),
    dashboardFilters.selectors.selectFilters,
    (botsData, filtersSlice) => {
      return filterBots(botsData, filtersSlice.filters);
    }
  );

export const dashboardSelectors = {
  selectConsolidatedPortfolioHistoryData,
  selectAccountChartData,
  selectOpenTrades,
  selectBotsData,
};
